import React, { useState, useEffect, useCallback } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Search,
  Eye,
  Check,
  X,
  Download,
  Users as UsersIcon,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  Building,
  Ban,
  ShieldCheck,
  CheckCircle,
  AlertCircle,
  UserPlus,
  UserMinus,
} from "lucide-react";

import "./ManageUser.css";
import "../ManageAuctions/ManageAuctionsPro.css";
import "./ManageUsersPro.css";
import userService, { NormalizedUserRecord } from '../../../services/userService';
import adminChannelPartnerService from '../../../services/adminChannelPartnerService';

type User = NormalizedUserRecord;

const ManageUsers: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  // "enabled" = every account that is not blocked; "inactive" = blocked
  const [filterStatus, setFilterStatus] = useState<"all" | "enabled" | "inactive">("all");
  const [filterRole, setFilterRole] = useState<"all" | "user" | "channel_partner">("all");
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // the search box is debounced so we don't query on every keystroke
  const [searchInput, setSearchInput] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [confirmBlockUser, setConfirmBlockUser] = useState<User | null>(null);
  const [confirmCpChange, setConfirmCpChange] = useState<{ user: User; action: "promote" | "revoke" } | null>(null);
  const [confirmUnblockUser, setConfirmUnblockUser] = useState<User | null>(null); // New state for unblock popup
  const [successModal, setSuccessModal] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: '' });
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: '' });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await userService.listUsers({
        page,
        limit,
        search: searchTerm.trim(),
        status: filterStatus === 'all' ? '' : filterStatus,
        role: filterRole === 'all' ? '' : filterRole,
      });
      setUsers(result.users);
      setTotalUsers(result.total);
      setStatusCounts(result.statusCounts || {});
    } catch (e: any) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, filterStatus, filterRole]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, refreshIndex]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);
  
  const handleExportUsers = async () => {
    if (!users.length) {
      setErrorModal({ isOpen: true, message: "No users to export" });
      return;
    }

    try {
      // Generate HTML for the report
      const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Users Report</title>
  <style>
    * { 
      box-sizing: border-box; 
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    html, body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      margin: 0;
      padding: 0;
      width: 297mm; /* A4 landscape width */
      font-size: 10pt;
      color: #1f2937;
      background: #ffffff;
      line-height: 1.4;
    }
    .pdf-container {
      width: 297mm;
      padding: 15mm;
      background: white;
      box-sizing: border-box;
    }
    
    .report-header {
      background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
      color: white;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 20px;
    }
    .company-name {
      font-size: 28px;
      font-weight: bold;
      margin: 0;
    }
    .report-title {
      font-size: 18px;
      margin: 8px 0 0;
    }
    .report-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 10px;
      font-size: 11px;
    }
    
    table {
      border-collapse: collapse;
      width: 100%;
      margin-top: 10px;
      font-size: 9pt;
      background: white;
      border: 1px solid #e5e7eb;
    }
    th {
      background: #4f46e5 !important;
      color: white !important;
      font-weight: 600;
      padding: 10px 6px;
      text-align: left;
      border: 1px solid #3730a3;
      white-space: nowrap;
    }
    td {
      border: 1px solid #e5e7eb;
      padding: 8px 6px;
      text-align: left;
      line-height: 1.3;
      vertical-align: middle;
    }
    tbody tr:nth-child(even) {
      background: #f9fafb !important;
    }
    
    .status-active { color: #047857; font-weight: 600; }
    .status-pending { color: #d97706; font-weight: 600; }
    .status-blocked { color: #dc2626; font-weight: 600; }
  </style>
</head>
<body>
  <div class="pdf-container">
    <div class="report-header">
      <h1 class="company-name">EasyeAuction</h1>
      <div class="report-title">Users Report</div>
      <div class="report-meta">
        <div>Generated: ${currentDate}</div>
        <div>Total Users: ${users.length}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Company</th>
          <th>Role</th>
          <th>Status</th>
          <th>City</th>
          <th style="text-align: center;">Auctions</th>
          <th style="text-align: center;">Bids</th>
          <th style="text-align: center;">Wins</th>
        </tr>
      </thead>
      <tbody>
        ${users.map((user, index) => {
          const statusClass = 
            user.status === 'inactive' ? 'status-blocked' :
            user.status === 'active' ? 'status-active' :
            user.status === 'pending' ? 'status-pending' : '';
          
          return `
            <tr>
              <td style="text-align: center; font-weight: 600;">${index + 1}</td>
              <td>${user.name || 'N/A'}</td>
              <td>${user.email || 'N/A'}</td>
              <td>${user.phoneNumber || 'N/A'}</td>
              <td>${user.companyName || 'N/A'}</td>
              <td>${user.role === 'participant' ? 'User' : user.role || 'N/A'}</td>
              <td class="${statusClass}">
                ${user.status === 'inactive' ? 'Blocked' : 
                  user.status === 'active' ? 'Active' : 
                  user.status || 'N/A'}
              </td>
              <td>${user.city || 'N/A'}</td>
              <td style="text-align: center;">${user.totalAuctions || 0}</td>
              <td style="text-align: center;">${user.totalBids || 0}</td>
              <td style="text-align: center;">${user.winningBids || 0}</td>
            </tr>
          `;
        }).join('')}
      </tbody>
    </table>
  </div>
</body>
</html>`;

      // Create an invisible iframe for rendering
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-99999px';
      iframe.style.top = '-99999px';
      iframe.style.width = '1123px'; // A4 landscape width in pixels
      iframe.style.height = '794px'; // A4 landscape height in pixels
      iframe.style.border = 'none';
      iframe.style.visibility = 'hidden';
      
      document.body.appendChild(iframe);
      
      // Write HTML to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        document.body.removeChild(iframe);
        throw new Error('Cannot access iframe document');
      }
      
      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();
      
      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Get the container
      const container = iframeDoc.querySelector('.pdf-container') as HTMLElement;
      if (!container) {
        document.body.removeChild(iframe);
        throw new Error('PDF container not found');
      }
      
      // Capture as canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: 1123,
        height: 794
      });
      
      // Create PDF
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, '', 'FAST');
      
      // Remove iframe
      document.body.removeChild(iframe);
      
      // Save the PDF
      const fileName = `users_report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      setSuccessModal({ isOpen: true, message: 'Users report exported successfully!' });
    } catch (error) {
      console.error('Export error:', error);
      setErrorModal({ isOpen: true, message: 'Failed to export users report. Please try again.' });
    }
  };

  const handleBlockUser = async (userId: string) => {
    try {
      await userService.blockUser(userId); // Call the new service function
      setRefreshIndex(i => i + 1); // Refresh the user list
      setSuccessModal({ isOpen: true, message: 'User blocked successfully!' });
    } catch (e: any) {
      console.error('Failed to block user:', e);
      setErrorModal({ isOpen: true, message: e.message || 'Failed to block user. Please try again.' });
    }
  };

  const handleUnblockUser = async (userId: string) => {
    try {
      await userService.unblockUser(userId); // Call the unblock service function
      setRefreshIndex(i => i + 1); // Refresh the user list
      setSuccessModal({ isOpen: true, message: 'User unblocked successfully!' });
    } catch (e: any) {
      console.error('Failed to unblock user:', e);
      setErrorModal({ isOpen: true, message: e.message || 'Failed to unblock user. Please try again.' });
    }
  };

  // Only plain user accounts can be promoted; company viewers are view-only
  // accounts managed by the channel partner who added them.
  const canPromote = (u: User) => u.role !== "channel_partner" && u.role !== "auction_viewer";

  const handleCpChange = async (target: User, action: "promote" | "revoke") => {
    try {
      if (action === "promote") {
        await adminChannelPartnerService.promote(target.id);
      } else {
        await adminChannelPartnerService.revoke(target.id);
      }
      setRefreshIndex((i) => i + 1);
      setSuccessModal({
        isOpen: true,
        message:
          action === "promote"
            ? `${target.name} is now a channel partner. They keep their normal user access and gain the channel partner features.`
            : `${target.name} is no longer a channel partner and is back to a regular user.`,
      });
    } catch (e: any) {
      setErrorModal({ isOpen: true, message: e.message || "Failed to update channel partner access. Please try again." });
    }
  };

  const handleViewDetails = async (user: User) => {
    try {
      const full = await userService.getUserById(user.id);
      setSelectedUser(full);
    } catch (e) {
      console.warn('Falling back to list user details due to error', e);
      setSelectedUser(user);
    }
  };
  const formatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return '';

    // 1. Remove non-numeric characters first, keeping only digits
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');

    // 2. Check if the number starts with the country code '91'
    const isIndianNumber = cleaned.startsWith('91');

    if (isIndianNumber && cleaned.length === 12) {
      // Matches '+91' followed by 10 digits
      const formatted = cleaned.slice(2); // Get the last 10 digits
      const match = formatted.match(/^(\d{5})(\d{5})$/);
      if (match) {
        return `+91 ${match[1]} ${match[2]}`;
      }
    }

    // 3. Handle a raw 10-digit number
    if (cleaned.length === 10) {
      const match = cleaned.match(/^(\d{5})(\d{5})$/);
      if (match) {
        return `+91 ${match[1]} ${match[2]}`;
      }
    }

    // 4. Return the original string if no format matches
    return phoneNumber;
  };
  const isBlocked = (u: User) => u.status === "blocked" || u.status === "inactive";
  const statusKey = (u: User) => (isBlocked(u) ? "blocked" : "active");
  const roleLabel = (role: string) =>
    role === "channel_partner"
      ? "Channel Partner"
      : role === "auctioneer"
      ? "Auctioneer"
      : "User";
  const initials = (name: string) =>
    (name || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("") || "?";
  const formatDate = (v?: string) => {
    if (!v) return "-";
    const d = new Date(v.replace(" ", "T"));
    return isNaN(d.getTime())
      ? "-"
      : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const rangeStart = users.length ? (page - 1) * limit + 1 : 0;
  const rangeEnd = (page - 1) * limit + users.length;
  const hasNext = page * limit < totalUsers;
  const totalPages = Math.max(1, Math.ceil(totalUsers / limit));
  const allCount = statusCounts.total ?? null;
  const tabs: Array<{ value: typeof filterStatus; label: string; count: number | null }> = [
    { value: "all", label: "All", count: allCount },
    { value: "enabled", label: "Active", count: statusCounts.enabled ?? null },
    { value: "inactive", label: "Blocked", count: statusCounts.inactive ?? null },
  ];

  const pageNumbers = (): Array<number | "…"> => {
    const pages: Array<number | "…"> = [];
    const win = new Set([1, totalPages, page - 1, page, page + 1]);
    let last = 0;
    for (let n = 1; n <= totalPages; n++) {
      if (win.has(n)) {
        if (n - last > 1) pages.push("…");
        pages.push(n);
        last = n;
      }
    }
    return pages;
  };

  const changeStatus = (v: typeof filterStatus) => {
    setFilterStatus(v);
    setPage(1);
  };

  const confirmDialog = (
    kind: "block" | "unblock",
    user: User,
    onClose: () => void,
    onConfirm: () => void
  ) => (
    <div className="mau-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="mau-modal mau-modal-sm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="mau-modal-head">
          <div>
            <h2>{kind === "block" ? "Block user" : "Unblock user"}</h2>
          </div>
          <button type="button" className="mau-icon-btn" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="mau-modal-body">
          <p className="mau-confirm-text">
            Are you sure you want to {kind} <strong>{user.name}</strong>?{" "}
            {kind === "block"
              ? "They will no longer be able to access their account."
              : "Their access to the account will be restored."}
          </p>
        </div>
        <div className="mau-modal-foot">
          <button type="button" className="mau-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={`mau-btn ${kind === "block" ? "mau-btn-danger" : "mau-btn-ok"}`} onClick={onConfirm}>
            {kind === "block" ? <Ban /> : <ShieldCheck />}
            {kind === "block" ? "Yes, block" : "Yes, unblock"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mau-root">
      {/* ---------- Header ---------- */}
      <header className="mau-hero">
        <div>
          <h1 className="mau-title">
            <span className="mau-title-icon">
              <UsersIcon />
            </span>
            Manage Users
          </h1>
          <p className="mau-subtitle">View, search and manage the people registered on the platform</p>
        </div>
        <div className="mau-hero-actions">
          <button type="button" className="mau-btn" onClick={() => setRefreshIndex((i) => i + 1)} disabled={loading}>
            <RefreshCcw className={loading ? "mau-spin" : ""} />
            Refresh
          </button>
          <button type="button" className="mau-btn mau-btn-primary" onClick={handleExportUsers}>
            <Download />
            Export PDF
          </button>
        </div>
      </header>

      {error && (
        <div className="mau-banner mau-banner-error" role="alert">
          <AlertCircle />
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshIndex((i) => i + 1)}>
            Retry
          </button>
        </div>
      )}

      {/* ---------- Tabs + filters ---------- */}
      <section className="mau-card mau-flush">
        <div className="mau-toolbar">
          <div className="mau-tabs" role="tablist" aria-label="Filter by status">
            {tabs.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={filterStatus === t.value}
                className={`mau-tab ${filterStatus === t.value ? "active" : ""}`}
                onClick={() => changeStatus(t.value)}
              >
                {t.label}
                {t.count !== null && <span className="mau-tab-count">{t.count}</span>}
              </button>
            ))}
          </div>
          <div className="mau-toolbar-right">
            <select
              className="mau-select"
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value as typeof filterRole);
                setPage(1);
              }}
              aria-label="Filter by role"
            >
              <option value="all">All roles</option>
              <option value="user">Users</option>
              <option value="channel_partner">Channel partners</option>
            </select>
            <div className="mau-search">
              <Search />
              <input
                type="text"
                placeholder="Search name, company, phone or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ---------- Table ---------- */}
        {loading && users.length === 0 ? (
          <div className="mau-state">
            <div className="mau-spinner" />
            <span>Loading users...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="mau-state">
            <UsersIcon />
            <strong>No users found</strong>
            <span>Try a different status, role or search term.</span>
          </div>
        ) : (
          <div className={`mau-table-wrap ${loading ? "mau-fade" : ""}`}>
            <table className="mau-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Company</th>
                  <th>Role</th>
                  <th className="r">Auctions</th>
                  <th className="r">Bids</th>
                  <th className="r">Wins</th>
                  <th>Status</th>
                  <th className="r">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td data-label="User">
                      <div className="mau-person">
                        <span className="mau-avatar" aria-hidden="true">
                          {initials(user.name)}
                        </span>
                        <div>
                          <div className="mau-cell-title">{user.name}</div>
                          <div className="mau-cell-sub">{formatPhoneNumber(user.phoneNumber)}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Company">
                      <div className="mau-cell-title">{user.companyName}</div>
                      <div className="mau-cell-sub">{user.email}</div>
                    </td>
                    <td data-label="Role">
                      <span className={`mau-role ${user.role === "channel_partner" ? "mau-role-cp" : ""}`}>{roleLabel(user.role)}</span>
                      <div className="mau-cell-sub">Joined {formatDate(user.registrationDate)}</div>
                    </td>
                    <td className="r" data-label="Auctions">{user.totalAuctions}</td>
                    <td className="r" data-label="Bids">{user.totalBids}</td>
                    <td className="r" data-label="Wins">{user.winningBids}</td>
                    <td data-label="Status">
                      <span className={`mau-badge mau-badge-${statusKey(user) === "blocked" ? "rejected" : "live"}`}>
                        {statusKey(user) === "blocked" ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td className="r" data-label="Actions">
                      <div className="mau-actions">
                        <button
                          type="button"
                          onClick={() => handleViewDetails(user)}
                          className="mau-icon-btn"
                          title="View details"
                          aria-label={`View details for ${user.name}`}
                        >
                          <Eye />
                        </button>
                        {user.role === "channel_partner" ? (
                          <button
                            type="button"
                            onClick={() => setConfirmCpChange({ user, action: "revoke" })}
                            className="mau-icon-btn"
                            title="Remove channel partner access"
                            aria-label={`Remove channel partner access for ${user.name}`}
                          >
                            <UserMinus />
                          </button>
                        ) : canPromote(user) ? (
                          <button
                            type="button"
                            onClick={() => setConfirmCpChange({ user, action: "promote" })}
                            className="mau-icon-btn mau-icon-ok"
                            title="Make channel partner"
                            aria-label={`Make ${user.name} a channel partner`}
                          >
                            <UserPlus />
                          </button>
                        ) : null}
                        {!isBlocked(user) ? (
                          <button
                            type="button"
                            onClick={() => setConfirmBlockUser(user)}
                            className="mau-icon-btn mau-icon-danger"
                            title="Block user"
                            aria-label={`Block ${user.name}`}
                          >
                            <Ban />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmUnblockUser(user)}
                            className="mau-icon-btn mau-icon-ok"
                            title="Unblock user"
                            aria-label={`Unblock ${user.name}`}
                          >
                            <ShieldCheck />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ---------- Pagination ---------- */}
        {totalUsers > 0 && (
          <div className="mau-pager">
            <span className="mau-pager-info">
              Showing {rangeStart}-{rangeEnd} of {totalUsers}
            </span>
            <div className="mau-pager-buttons">
              <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">
                <ChevronLeft />
              </button>
              {pageNumbers().map((n, i) =>
                n === "…" ? (
                  <span key={`gap-${i}`} className="mau-pager-gap">
                    …
                  </span>
                ) : (
                  <button key={n} type="button" className={n === page ? "active" : ""} onClick={() => setPage(n)}>
                    {n}
                  </button>
                )
              )}
              <button type="button" disabled={!hasNext || loading} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                <ChevronRight />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ---------- Confirm dialogs ---------- */}
      {confirmBlockUser &&
        confirmDialog(
          "block",
          confirmBlockUser,
          () => setConfirmBlockUser(null),
          () => {
            handleBlockUser(confirmBlockUser.id);
            setConfirmBlockUser(null);
          }
        )}
      {confirmUnblockUser &&
        confirmDialog(
          "unblock",
          confirmUnblockUser,
          () => setConfirmUnblockUser(null),
          () => {
            handleUnblockUser(confirmUnblockUser.id);
            setConfirmUnblockUser(null);
          }
        )}

      {confirmCpChange && (
        <div className="mau-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmCpChange(null)}>
          <div className="mau-modal mau-modal-sm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="mau-modal-head">
              <div>
                <h2>{confirmCpChange.action === "promote" ? "Make channel partner" : "Remove channel partner"}</h2>
              </div>
              <button type="button" className="mau-icon-btn" onClick={() => setConfirmCpChange(null)} aria-label="Close">
                <X />
              </button>
            </div>
            <div className="mau-modal-body">
              <p className="mau-confirm-text">
                {confirmCpChange.action === "promote" ? (
                  <>
                    Make <strong>{confirmCpChange.user.name}</strong> a channel partner? They will keep everything they can
                    do as a user (create, join and bid in auctions) and also get the channel partner features.
                  </>
                ) : (
                  <>
                    Remove channel partner access from <strong>{confirmCpChange.user.name}</strong>? Their account and
                    auction history stay intact; they simply go back to being a regular user.
                  </>
                )}
              </p>
            </div>
            <div className="mau-modal-foot">
              <button type="button" className="mau-btn" onClick={() => setConfirmCpChange(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={`mau-btn ${confirmCpChange.action === "promote" ? "mau-btn-ok" : "mau-btn-danger"}`}
                onClick={() => {
                  const { user, action } = confirmCpChange;
                  setConfirmCpChange(null);
                  handleCpChange(user, action);
                }}
              >
                {confirmCpChange.action === "promote" ? <UserPlus /> : <UserMinus />}
                {confirmCpChange.action === "promote" ? "Yes, make channel partner" : "Yes, remove access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Details modal ---------- */}
      {selectedUser && (
        <div className="mau-overlay" onClick={(e) => e.target === e.currentTarget && setSelectedUser(null)}>
          <div className="mau-modal" role="dialog" aria-modal="true" aria-label="User details" onClick={(e) => e.stopPropagation()}>
            <div className="mau-modal-head">
              <div className="mau-person">
                <span className="mau-avatar mau-avatar-lg" aria-hidden="true">
                  {initials(selectedUser.name)}
                </span>
                <div>
                  <h2>{selectedUser.name}</h2>
                  <p>
                    <span className={`mau-role ${selectedUser.role === "channel_partner" ? "mau-role-cp" : ""}`}>{roleLabel(selectedUser.role)}</span>
                    <span className={`mau-badge mau-badge-${isBlocked(selectedUser) ? "rejected" : "live"}`}>
                      {isBlocked(selectedUser) ? "Blocked" : "Active"}
                    </span>
                  </p>
                </div>
              </div>
              <button type="button" className="mau-icon-btn" onClick={() => setSelectedUser(null)} aria-label="Close">
                <X />
              </button>
            </div>

            <div className="mau-modal-body">
              <div className="mau-tiles mau-tiles-3">
                <div className="mau-tile">
                  <span>Auctions created</span>
                  <strong>{selectedUser.totalAuctions}</strong>
                </div>
                <div className="mau-tile">
                  <span>Total bids</span>
                  <strong>{selectedUser.totalBids}</strong>
                </div>
                <div className="mau-tile">
                  <span>Auctions won</span>
                  <strong>{selectedUser.winningBids}</strong>
                </div>
              </div>

              <div className="mau-sections">
                <section className="mau-section">
                  <h3>
                    <Phone /> Contact
                  </h3>
                  <dl>
                    <div><dt>Phone</dt><dd>{formatPhoneNumber(selectedUser.phoneNumber)}</dd></div>
                    <div><dt>Email</dt><dd>{selectedUser.email}</dd></div>
                  </dl>
                </section>
                <section className="mau-section">
                  <h3>
                    <Building /> Company
                  </h3>
                  <dl>
                    <div><dt>Name</dt><dd>{selectedUser.companyName}</dd></div>
                    <div><dt>Address</dt><dd>{selectedUser.companyAddress}</dd></div>
                  </dl>
                </section>
                <section className="mau-section mau-section-wide">
                  <h3>
                    <Mail /> Account
                  </h3>
                  <dl>
                    <div><dt>Registered</dt><dd>{formatDate(selectedUser.registrationDate)}</dd></div>
                    <div><dt>Last login</dt><dd>{selectedUser.lastLogin && selectedUser.lastLogin !== "—" ? formatDate(selectedUser.lastLogin) : "-"}</dd></div>
                  </dl>
                </section>
              </div>
            </div>

            <div className="mau-modal-foot">
              {selectedUser.role === "channel_partner" ? (
                <button
                  type="button"
                  className="mau-btn"
                  onClick={() => {
                    const target = selectedUser;
                    setSelectedUser(null);
                    setConfirmCpChange({ user: target, action: "revoke" });
                  }}
                >
                  <UserMinus />
                  Remove channel partner
                </button>
              ) : canPromote(selectedUser) ? (
                <button
                  type="button"
                  className="mau-btn mau-btn-ok"
                  onClick={() => {
                    const target = selectedUser;
                    setSelectedUser(null);
                    setConfirmCpChange({ user: target, action: "promote" });
                  }}
                >
                  <UserPlus />
                  Make channel partner
                </button>
              ) : null}
              {!isBlocked(selectedUser) ? (
                <button
                  type="button"
                  className="mau-btn mau-btn-danger"
                  onClick={() => {
                    setSelectedUser(null);
                    setConfirmBlockUser(selectedUser);
                  }}
                >
                  <Ban />
                  Block user
                </button>
              ) : (
                <button
                  type="button"
                  className="mau-btn mau-btn-ok"
                  onClick={() => {
                    setSelectedUser(null);
                    setConfirmUnblockUser(selectedUser);
                  }}
                >
                  <ShieldCheck />
                  Unblock user
                </button>
              )}
              <button type="button" className="mau-btn" onClick={() => setSelectedUser(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Result dialogs ---------- */}
      {successModal.isOpen && (
        <div className="mau-overlay" onClick={() => setSuccessModal({ isOpen: false, message: "" })}>
          <div className="mau-modal mau-modal-sm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="mau-modal-body mau-result">
              <CheckCircle className="mau-result-ok" />
              <h2>Success</h2>
              <p>{successModal.message}</p>
            </div>
            <div className="mau-modal-foot">
              <button type="button" className="mau-btn mau-btn-primary" onClick={() => setSuccessModal({ isOpen: false, message: "" })}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {errorModal.isOpen && (
        <div className="mau-overlay" onClick={() => setErrorModal({ isOpen: false, message: "" })}>
          <div className="mau-modal mau-modal-sm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="mau-modal-body mau-result">
              <AlertCircle className="mau-result-err" />
              <h2>Something went wrong</h2>
              <p>{errorModal.message}</p>
            </div>
            <div className="mau-modal-foot">
              <button type="button" className="mau-btn mau-btn-danger" onClick={() => setErrorModal({ isOpen: false, message: "" })}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
